<script setup lang="ts">
//#region import
import { computed, ref, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { cloneDeep, debounce } from 'lodash-es';
import { endOfDay, format, startOfDay } from 'date-fns';
import { useTypedStore } from '@/store';
import { useApi } from '@/api';

import type { EmitsToListeners, SortParams, TableMethods } from '@/types/core';
import type {
  GetAffiliatesData,
  GetAffiliatesResponse,
  PostAffiliatesRearrangeReferralProgramAvailableAffiliatesResponse, PostAffiliatesRearrangeReferralProgramCountData,
  PostAffiliatesRearrangeReferralProgramData,
} from '@/types/openapi-ts';
//#endregion

//#region type
export interface Props {
  currency?: string | null;
}

export interface  Emits {
  'close': [],
  'success': [],
}

export interface TransferAffiliatesParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}

type AffiliatesKey = 'to' | 'from';

type Affiliates = Record<AffiliatesKey, {
  current: string,
  list: PostAffiliatesRearrangeReferralProgramAvailableAffiliatesResponse['payload'],
  loading: boolean,
}>;
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  currency: null,
});
const emit = defineEmits<Emits>();
//#endregion

//#region const
const fields = [
  {
    name: 'incrementing_id',
    sortable: 'custom',
    width: '70',
  },
  {
    name: 'name',
    computedValue: (row: GetAffiliatesResponse['payload'][number]) => {
      return `${row.first_name || ''} ${row.last_name || ''}`;
    },
  },
  {
    name: 'email',
    sortable: 'custom',
  },
];

const tabsList = [
  {
    value: '<=',
    name: '≤',
  },
  {
    value: '>=',
    name: '≥',
  },
];
//#endregion

//#region composables
const api = useApi();
const store = useTypedStore();
const { t } = useI18n();
//#endregion

//#region data
const tableRef = ref<TableMethods | null>(null);
const isVisible = ref(false);
const isLoading = ref(false);
const errors = ref<any>({});

const isDataLoading = ref(false);
const searchQuery = ref('');
const sortProp = ref('incrementing_id');
const sortOrder = ref('desc');
const page = ref(1);
const limit = ref(10);
const count = ref(0);
const tableData = ref<GetAffiliatesResponse['payload']>([]);

const subAffiliates = ref('all');
const lastMonthRevenue = ref('<=');
const balance = ref('<=');
const isLoadingCounter = ref(false);

const counterSubAffiliates = ref(0);
const fromAffiliateReferralId = ref<string | null>(null);
const selectedCountries = ref<string[]>([]);
const lastMonthRevenueAmount = ref(null);
const lastMonthRevenueCurrency = ref(props.currency);
const balanceAmount = ref(null);
const balanceCurrency = ref(props.currency);
const selectedDirectAffiliates = ref<string[]>([]);
const dateRange = ref([]);

const affiliates = reactive<Affiliates>({
  from: {
    current: '',
    list: [],
    loading: false,
  },
  to: {
    current: '',
    list: [],
    loading: false,
  },
});
//#endregion

//#region computed
const countries = computed(() => store.getters['misc/countries']);
const customerCurrencies = computed(() => store.getters['misc/customerCurrencies']);

const subAffiliatesList = computed(() => {
  return [
    {
      label: t('affiliates.transfer_popup.all'),
      value: 'all',
    },
    {
      label: t('affiliates.transfer_popup.conditional_filter'),
      value: 'selected',
    },
    {
      label: t('affiliates.transfer_popup.direct_selection'),
      value: 'direct',
    },
  ];
});

const computedCounterSubAffiliates = computed(() => {
  if (subAffiliates.value === 'direct') {
    return selectedDirectAffiliates.value.length;
  }
  return counterSubAffiliates.value;
});

const fetchParams = computed(() => {
  const startDate = dateRange?.value?.[0];
  const endDate = dateRange?.value?.[1];

  return {
    ...(affiliates.from.current && { affiliate_id_from: affiliates.from.current }),
    ...(subAffiliates.value === 'direct' && {
      referral_id: selectedDirectAffiliates.value,
    }),
    ...(subAffiliates.value === 'selected' && {
      filters: {
        ...(selectedCountries?.value?.length && {
          country_code: {
            op: '=',
            value: selectedCountries.value,
          },
        }),
        ...(balanceAmount.value !== null && balanceAmount.value !== '' && {
          balance: {
            op: balance.value,
            value: balanceAmount.value,
          },
        }),
        ...(lastMonthRevenueAmount.value !== null && lastMonthRevenueAmount.value !== '' && {
          last_month_revenue: {
            op: lastMonthRevenue.value,
            value: lastMonthRevenueAmount.value,
          },
        }),
        last_month_revenue_currency_code: lastMonthRevenueCurrency.value,
        balance_currency_code: balanceCurrency.value,
        ...(startDate && endDate ? {
          registration_date: {
            op: 'between',
            value: [
              format(startOfDay(startDate), 'yyyy-MM-dd HH:mm:ss'),
              format(endOfDay(endDate), 'yyyy-MM-dd HH:mm:ss'),
            ],
          },
        } : {}),
      },
    }),
  } as PostAffiliatesRearrangeReferralProgramCountData['body'];
});
//#endregion

//#region watch
watch(fetchParams, (newVal) => {
  if (affiliates.from.current && subAffiliates.value !== 'direct') {
    isLoadingCounter.value = true;
    searchQuery.value = '';
    fetchData(newVal);
  }
});
watch(() => affiliates.from.current, (newVal) => {
  if (newVal && affiliates.to.current) {
    affiliates.to.current = '';
  }

  tableData.value = [];

  if (subAffiliates.value === 'direct') {
    resetDirectAffiliates();
    fetchDirectSubAffiliates();
  }
});
//#endregion

//#region methods
function open() {
  getAffiliates('from');
  isVisible.value = true;
}

function handleClose() {
  emit('close');
  isVisible.value = false;
  errors.value = {};
}

function handleFromAffiliate(val: string) {
  const affiliate = affiliates.from.list.find((item) => item.id === val);
  fromAffiliateReferralId.value = affiliate!.referral_id;
}

function getAffiliates(type: AffiliatesKey, search = '') {
  const query = {
    limit: 100,
    offset: 0,
    search,
    referral_program_enabled: true,
    only_editable: true,
  };
  const body = {
    affiliate_id_from: affiliates.from.current,
  };

  const promise = type === 'from'
    ? api.getAffiliates(query)
    : api.availableAffiliateReferralProgram(body, query);

  promise
    .then((res) => {
      affiliates[type].list = cloneDeep(res.data.payload) as PostAffiliatesRearrangeReferralProgramAvailableAffiliatesResponse['payload'];
    })
    .catch((e) => {

    }).finally(() => {
      affiliates[type].loading = false;
    });
}

const getAffiliatesDebounced = debounce((type, search) => {
  getAffiliates(type, search);
}, 800);

function searchMethodFrom(search: string) {
  affiliates.from.loading = true;
  getAffiliatesDebounced('from', search);
}

function searchMethodTo(search: string) {
  affiliates.to.loading = true;
  getAffiliatesDebounced('to', search);
}

function preventSymbol(e: KeyboardEvent) {
  if (['e', 'E', '+', '-'].includes(e.key)) {
    e.preventDefault();
  }
}

function changeAffiliate(val: string | number | boolean | undefined) {
  if (val === 'direct') {
    resetDirectAffiliates();
    fetchDirectSubAffiliates();
  }
}
function resetDirectAffiliates() {
  if (tableRef.value) {
    tableRef.value.clearCheck();
  }
  tableData.value = [];
}
function fetchDirectSubAffiliates() {
  if (!affiliates.from.current) return;
  const query = {
    sort_column: sortProp.value,
    sort_dir: sortOrder.value,
    search: searchQuery.value,
    limit: limit.value,
    offset: limit.value * page.value - limit.value,
    referred_by: fromAffiliateReferralId.value,
  } as GetAffiliatesData['query'];

  api.getAffiliates(query).then((res) => {
    count.value = res.data.misc!.count;
    tableData.value = res.data.payload;
  });
}

function handleRefresh() {
  page.value = 1;
  fetchDirectSubAffiliates();
}

function pageChange(newPage: number, newSize: number) {
  if (page.value !== newPage || limit.value !== newSize) {
    page.value = newPage;
    limit.value = newSize;
    fetchDirectSubAffiliates();
  }
}

function handleSort({ prop, order }: SortParams) {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  fetchDirectSubAffiliates();
}

function checkedDirectRows(id: string, ids: string[]) {
  selectedDirectAffiliates.value = ids;
}

function fetchData(value: PostAffiliatesRearrangeReferralProgramCountData['body']) {
  api.updateAffiliateReferralProgramCount(value).then((res) => {
    counterSubAffiliates.value = res?.data?.payload?.count || 0;
  }).finally(() => isLoadingCounter.value = false);
}

function handleSave() {
  isLoading.value = true;

  const params = {
    ...fetchParams.value,
    ...(affiliates.to.current && { affiliate_id_to: affiliates.to.current }),
  } as PostAffiliatesRearrangeReferralProgramData['body'];

  api.updateAffiliateReferralProgram(params).then(() => {
    emit('success');
    handleClose();
  }).catch((e) => {
    errors.value = e.data.errors;
  }).finally(()=> {
    isLoading.value = false;
  });
}
//#endregion

defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="transfer-sub-affiliates-popup"
    :title="$t('affiliates.transfer_popup.transferSubAffiliates')"
    :width="798"
    :is-loading="isLoading"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <div class="t:flex t:items-start t:space-x-8">
          <UiSelectElement
            v-model="affiliates.from.current"
            class="t:flex-1"
            :placeholder="$t('affiliates.transfer_popup.placeholder_affiliate')"
            :label="$t('affiliates.transfer_popup.from')"
            :list="affiliates.from.list"
            :loading="affiliates.from.loading"
            :remote-method="searchMethodFrom"
            remote
            filterable
            option-label-param="email"
            option-name-param="email"
            width="100%"
            size="big"
            :error="errors.affiliate_id_from"
            @change="handleFromAffiliate"
          />
          <UiIcon
            :size="16"
            color="var(--text-body-secondary)"
            name="arrow-right-long"
            class="t:mt-32"
          />
          <UiSelectElement
            v-model="affiliates.to.current"
            class="t:flex-1"
            :placeholder="$t('affiliates.transfer_popup.placeholder_affiliate')"
            :label="$t('affiliates.transfer_popup.to')"
            :list="affiliates.to.list"
            :loading="affiliates.to.loading"
            :remote-method="searchMethodTo"
            remote
            filterable
            :disabled="!affiliates.from.current"
            option-label-param="email"
            option-name-param="email"
            width="100%"
            size="big"
            :error="errors.affiliate_id_to || errors.hierarchy"
          />
        </div>
        <CardTitle>
          <template v-slot:title>
            <div
              v-if="!affiliates.from.current"
              class="transfer-sub-affiliates-popup__info"
              v-html="$t('affiliates.transfer_popup.emptyAffiliates')"
            />
            <div
              v-else
              class="transfer-sub-affiliates-popup__info"
            >
              <div
                v-loading="isLoadingCounter"
                class="transfer-sub-affiliates-popup__loader"
                :class="{ 'transfer-sub-affiliates-popup__loader_loading': isLoadingCounter }"
              >
                <div
                  v-if="!isLoadingCounter"
                >
                  {{ computedCounterSubAffiliates }}
                </div>
              </div>
              &nbsp;
              <div v-html="$t('affiliates.transfer_popup.transferSubAffiliatesCounter')" />
            </div>
          </template>
        </CardTitle>
        <UiSeparator />
        <UiRadioGroup
          v-model="subAffiliates"
          :label="$t('affiliates.transfer_popup.sub_affiliates')"
          :options="subAffiliatesList"
          @change="changeAffiliate"
        />
        <div
          v-if="subAffiliates === 'selected'"
          class="t:space-x-32 t:flex"
        >
          <div class="t:space-y-16 t:flex-1">
            <UiSelectElement
              v-model="selectedCountries"
              :label="$t('affiliates.transfer_popup.country')"
              :placeholder="$t('affiliates.transfer_popup.placeholder_country')"
              :list="countries"
              search-param="name"
              filterable
              :max-collapse-tags="1"
              collapse-tag-tooltip
              multiple
              option-value-param="code"
              :error="errors.country_codes"
              size="big"
            />

            <div class="t:space-y-8">
              <UiLabel
                :label="$t('affiliates.transfer_popup.earning')"
              />
              <div class="t:flex t:items-start t:space-x-8">
                <UiTabs
                  v-model="lastMonthRevenue"
                  :list="tabsList"
                  size="big"
                />
                <UiInput
                  v-model="lastMonthRevenueAmount"
                  type="number"
                  :width="140"
                  :placeholder="$t('affiliates.transfer_popup.amount')"
                  size="big"
                  :error="errors.filters?.last_month_revenue?.value"
                  @change="preventSymbol"
                />
                <UiSelectElement
                  v-model="lastMonthRevenueCurrency"
                  :placeholder="$t('permissions.offers.not_selected')"
                  :list="customerCurrencies"
                  size="big"
                  filterable
                  option-value-param="code"
                  :error="errors.filters?.last_month_revenue_currency_code"
                />
              </div>
            </div>
          </div>
          <div class="t:space-y-16 t:flex-1">
            <UiDatePicker
              v-model="dateRange"
              :placeholder="$t(`team_popup.see_stats_placeholder`)"
              :label="$t('affiliates.transfer_popup.registration_date')"
              auto-apply
              teleport
              size="big"
              :error="errors?.filters?.registration_date?.value"
            />
            <div class="t:space-y-8">
              <UiLabel
                :label="$t('affiliates.transfer_popup.balance')"
              />
              <div class="t:flex t:items-start t:space-x-8">
                <UiTabs
                  v-model="balance"
                  :list="tabsList"
                  size="big"
                />
                <UiInput
                  v-model="balanceAmount"
                  type="number"
                  :width="140"
                  :placeholder="$t('affiliates.transfer_popup.amount')"
                  size="big"
                  :error="errors.filters?.balance?.value"
                  @change="preventSymbol"
                />
                <UiSelectElement
                  v-model="balanceCurrency"
                  :placeholder="$t('permissions.offers.not_selected')"
                  :list="customerCurrencies"
                  size="big"
                  filterable
                  option-value-param="code"
                  :error="errors.filters?.balance_currency_code"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="subAffiliates === 'direct'"
          class="t:space-y-16"
        >
          <div class="t:flex t:space-x-16">
            <UiInput
              v-model.trim="searchQuery"
              width="100%"
              is-search
              @search="handleRefresh"
            />
            <UiPagination
              :page="page"
              :page-size="limit"
              :count="count"
              @page-change="pageChange"
            />
          </div>
          <UiTableNew
            ref="tableRef"
            v-loading="isDataLoading"
            :data="tableData"
            :fields="fields"
            :select-row="true"
            :sort="{
              prop: sortProp,
              order: sortOrder
            }"
            select-type="checkbox"
            :external-height="376"
            :padding="0"
            is-static
            i18n-path="affiliates.transfer_popup"
            checked-row-id="referral_id"
            checked-row
            :show-check-all="true"
            :rows-count="limit"
            element-loading-custom-class="data-loader-spinner"
            @checked-rows="checkedDirectRows"
            @sort="handleSort"
          />
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          :disabled="isLoading || (subAffiliates === 'direct' && !computedCounterSubAffiliates)"
          @click="handleSave"
        >
          {{ $t('affiliates.transfer_popup.transfer') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.transfer-sub-affiliates-popup{
  &__info{
    align-items: center;
    display: flex;
    span {
      font-weight: 400;
    }
  }
  &__loader{
    &_loading {
      display: inline-block;
      width: 16px;
      height: 16px;
    }
    .el-loading-mask {
      background-color: transparent;
    }
    .el-loading-spinner {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      margin-top: 0;
      transform: translateY(-50%);
    }
    .circular {
      height: 100%;
      width: 14px;
      .path {
        stroke: var(--text-primary);
        stroke-width: 4px;
      }
    }
  }
}
</style>
